import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, IconButton, Typography } from '@mui/material';
import {
  BusinessOutlined as BusinessIcon,
  GroupOutlined as TeamIcon,
  FormatListBulleted as ListViewIcon,
} from '@mui/icons-material';
import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { HierarchicalOrgTeamSelector } from '../HierarchicalOrgTeamSelector';

interface OrgEntry {
  id: string;
  name: string;
  role?: string;
  isImplicit?: boolean;
}

interface TeamEntry {
  id: string;
  name: string;
  role?: string;
}

export interface WorkspaceFrameProps {
  currentOrg?: OrgEntry | null;
  currentTeam?: TeamEntry | null;
  onCreateTeam: () => void;
  canCreateTeam: boolean;
  /** @deprecated No longer used; kept for backward compatibility with call sites */
  drawerWidth?: number;
}

const workspaceIconSx = (theme: Theme): SystemStyleObject<Theme> => ({
  width: 24,
  height: 24,
  borderRadius: '6px',
  background: 'rgba(34,181,220,.18)',
  color: theme.palette.brand.cyan,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const rolePillSx = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: '10px',
  fontWeight: 700,
  padding: '2px 7px',
  borderRadius: '999px',
  background: 'rgba(34,181,220,.16)',
  color: theme.palette.brand.cyanSoft,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
});

const workspaceButtonSx = (theme: Theme): SystemStyleObject<Theme> => ({
  flex: 1,
  padding: '8px 6px',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 600,
  height: 38,
  border: '1px solid rgba(255,255,255,0.18)',
  background: 'rgba(255,255,255,0.10)',
  color: '#fff',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  minWidth: 0,
  '&:hover': {
    background: 'rgba(34,181,220,0.14)',
    borderColor: 'rgba(34,181,220,0.40)',
    color: '#fff',
  },
});

export const WorkspaceFrame: React.FC<WorkspaceFrameProps> = ({
  currentOrg,
  currentTeam,
  onCreateTeam,
  canCreateTeam,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(true);

  return (
    <Box
      sx={{
        background: 'transparent',
        border: '1px solid rgba(255,255,255,.22)',
        borderRadius: '12px',
        padding: '12px',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        '&::before': { display: 'none' },
      }}
    >
      {/* Inline workspace selector + expand/collapse toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          marginBottom: '12px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={(theme) => ({
            flex: 1,
            '& .MuiOutlinedInput-root': {
              background: 'rgba(255,255,255,0.10)',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              height: 38,
              '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.30)' },
              '&.Mui-focused fieldset': { borderColor: theme.palette.brand.cyan, borderWidth: '1px' },
            },
            '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.75)' },
            '& .MuiSelect-select': { padding: '8px 32px 8px 12px !important' },
            '& em': { color: 'rgba(255,255,255,0.85)', fontStyle: 'normal' },
            '& .MuiTypography-body2': { color: '#fff !important' },
          })}
        >
          <HierarchicalOrgTeamSelector />
        </Box>
        <IconButton
          color="inherit"
          size="small"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-label={isExpanded ? 'Collapse workspace details' : 'Expand workspace details'}
          sx={{
            color: '#fff',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.15)',
            width: 32,
            height: 32,
            '&:hover': {
              background: 'rgba(34,181,220,0.14)',
              borderColor: 'rgba(34,181,220,0.40)',
            },
            '& svg': { color: '#fff' },
          }}
        >
          <ListViewIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Collapsible workspace details */}
      {isExpanded && (
        <>
          {/* Current Workspace label */}
          <Typography
            component="div"
            sx={{
              fontSize: '10.5px',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.75)',
              marginBottom: '8px',
              position: 'relative',
              zIndex: 1,
            }}
          >
            Current Workspace
          </Typography>

          {/* Org role row (explicit) */}
          {currentOrg && !currentOrg.isImplicit && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '7px 0',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Box sx={workspaceIconSx}>
                <BusinessIcon sx={{ fontSize: 13 }} />
              </Box>
              <Box
                sx={{
                  fontSize: '13.5px',
                  fontWeight: 700,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexWrap: 'wrap',
                }}
              >
                {currentOrg.name}
                {currentOrg.role && (
                  <Box component="span" sx={rolePillSx}>
                    {currentOrg.role}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Org row (implicit — no role badge) */}
          {currentOrg && currentOrg.isImplicit && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '7px 0',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Box sx={workspaceIconSx}>
                <BusinessIcon sx={{ fontSize: 13 }} />
              </Box>
              <Box
                sx={{
                  fontSize: '13.5px',
                  fontWeight: 700,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexWrap: 'wrap',
                }}
              >
                {currentOrg.name}
              </Box>
            </Box>
          )}

          {/* Team role row */}
          {currentTeam && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 0 7px',
                position: 'relative',
                zIndex: 1,
                borderTop: '1px solid rgba(255,255,255,.05)',
                marginTop: '2px',
              }}
            >
              <Box sx={workspaceIconSx}>
                <TeamIcon sx={{ fontSize: 13 }} />
              </Box>
              <Box
                sx={{
                  fontSize: '13.5px',
                  fontWeight: 700,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexWrap: 'wrap',
                }}
              >
                {currentTeam.name}
                {currentTeam.role && (
                  <Box component="span" sx={rolePillSx}>
                    {currentTeam.role}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Action buttons — always visible */}
      <Box sx={{ display: 'flex', gap: '4px', marginTop: '12px', position: 'relative', zIndex: 1 }}>
        <Button size="small" component={Link} to="/organizations/new" sx={workspaceButtonSx}>
          + New Org
        </Button>
        {canCreateTeam && (
          <Button size="small" onClick={onCreateTeam} sx={workspaceButtonSx}>
            + New Team
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default WorkspaceFrame;
